// useMarkActiveWell — the navigation → active-well bridge. Driven by the single
// app-wide tracker useActiveWellFromRoute (below), which resolves the well from the
// URL on EVERY route — so landing on ANY well-scoped page (a pad→well, a timeline, a
// job, a report, the schematic) marks it active, not just the wells list
// (PO 2026-08-04 / broadened 2026-08-05).
//
// Sets the pointer only when the active well actually CHANGES, so:
//  - a re-render (a job added, an edit) on the SAME well does NOT reset the
//    remembered jobId — you stay bound to the job you picked;
//  - moving to a DIFFERENT well resets jobId to null (setActiveWell replaces the
//    whole pointer) → the new well disambiguates fresh.
// Archived wells and preview sentinels never bind (mirrors the TimelineScreen
// active-job guard). No engineering/UOM data (Charter Abs-2/3 N/A).

import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { Well } from "../types";
import { getActiveWell, setActiveWell } from "../activeWellContext";
import { resolveActiveWellFromLocation } from "../resolveActiveWellFromLocation";
import { useWellData } from "./useWellData";

/** Mark `well` active. `jobId` is optional context for pages that know the exact
 *  job being viewed (a report screen): `undefined` = "don't touch the remembered
 *  job" (well-level pages); an explicit string/null = adopt that job. */
export function useMarkActiveWell(
  well: Well | undefined | null,
  jobId?: string | null,
): void {
  const wellId = well?.id;
  const padId = well?.fieldPadId;
  const isArchived = !!well?.archivedAt;

  useEffect(() => {
    if (!wellId || !padId || isArchived) return;
    // Preview/demo sentinels are not real navigable wells — never mark them active.
    if (wellId.startsWith("demo-")) return;
    const active = getActiveWell();
    if (active?.wellId === wellId) {
      // Same well: keep the remembered job unless the caller supplies one; still
      // re-sync padId (a well can be reassigned to another pad). Skip the write when
      // nothing changed (avoid a redundant notify / render).
      const nextJobId = jobId === undefined ? (active.jobId ?? null) : jobId;
      if (active.padId !== padId || active.jobId !== nextJobId) {
        setActiveWell({ padId, wellId, jobId: nextJobId });
      }
      return;
    }
    // Different well → adopt the supplied job (else well-level, no job).
    setActiveWell({ padId, wellId, jobId: jobId ?? null });
  }, [wellId, padId, isArchived, jobId]);
}

/** THE app-wide active-well tracker (mounted once in RootLayout). Resolves the well
 *  from the CURRENT URL — however you reached it (a Well id, a wellbore id, or a job
 *  id, in path or query) — and marks it active. This makes ANY well-scoped page
 *  (pad→well, a timeline, a job, a report, the schematic, …) set the active well, not
 *  just the wells list (PO 2026-08-05). A non-well route resolves to nothing → no-op. */
export function useActiveWellFromRoute(): void {
  const { pathname, search } = useLocation();
  const { wells } = useWellData();
  const { well, jobId } = useMemo(
    () => resolveActiveWellFromLocation(pathname, search, wells),
    [pathname, search, wells],
  );
  useMarkActiveWell(well, jobId);
}
